import React from 'react';
import { AlertTriangle, XCircle, RefreshCw } from 'lucide-react';
import { Badge } from '../ui/badge';
import { useLanguage } from '../../contexts/LanguageContext';

// Inventory translations
const INVENTORY_TRANSLATIONS = {
  en: { soldOut: 'Sold Out', lowStock: 'Low Stock', only: 'Only', left: 'left!', available: 'available' },
  es: { soldOut: 'Agotado', lowStock: 'Pocas unidades', only: 'Solo', left: '!', available: 'disponible' },
  fr: { soldOut: 'Épuisé', lowStock: 'Stock faible', only: 'Seulement', left: '!', available: 'disponible' },
  de: { soldOut: 'Ausverkauft', lowStock: 'Niedriger Bestand', only: 'Nur noch', left: '!', available: 'verfügbar' },
  it: { soldOut: 'Esaurito', lowStock: 'Scorte basse', only: 'Solo', left: '!', available: 'disponibile' },
  pt: { soldOut: 'Esgotado', lowStock: 'Estoque baixo', only: 'Apenas', left: '!', available: 'disponível' },
  ar: { soldOut: 'نفذ', lowStock: 'مخزون منخفض', only: 'فقط', left: '!', available: 'متاح' },
  zh: { soldOut: '售罄', lowStock: '库存不足', only: '仅剩', left: '!', available: '可用' },
  ko: { soldOut: '품절', lowStock: '재고 부족', only: '단', left: '개!', available: '개 가능' }
};

/**
 * InventoryBadge - Shows real-time inventory status
 * 
 * Displays visual indicators for:
 * - Low stock (yellow warning)
 * - Sold out (red badge)
 * - Live update indicator (pulsing dot)
 */
export function InventoryBadge({ 
  quantity, 
  status = 'available', 
  showQuantity = true,
  hasLiveUpdate = false,
  size = 'sm',
  className = ''
}) {
  const { locale } = useLanguage();
  const t = INVENTORY_TRANSLATIONS[locale] || INVENTORY_TRANSLATIONS.en;
  
  const isSoldOut = status === 'sold_out' || quantity <= 0;
  const isLowStock = status === 'low_stock' || (quantity > 0 && quantity <= 5);

  if (isSoldOut) {
    return (
      <Badge 
        variant="destructive" 
        className={`flex items-center gap-1 ${size === 'sm' ? 'text-xs' : 'text-sm'} ${className}`}
        data-testid="inventory-badge-sold-out"
      >
        <XCircle className={size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} />
        {t.soldOut}
        {hasLiveUpdate && (
          <span className="relative flex h-2 w-2 ml-1">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
          </span>
        )}
      </Badge>
    );
  }

  if (isLowStock) {
    return (
      <Badge 
        variant="warning"
        className={`flex items-center gap-1 bg-yellow-100 text-yellow-800 border-yellow-300 ${size === 'sm' ? 'text-xs' : 'text-sm'} ${className}`}
        data-testid="inventory-badge-low-stock"
      >
        <AlertTriangle className={size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} />
        {showQuantity ? `${t.only} ${quantity} ${t.left}` : t.lowStock}
        {hasLiveUpdate && (
          <span className="relative flex h-2 w-2 ml-1">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-600 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-600"></span>
          </span>
        )}
      </Badge>
    );
  }

  // Normal stock - optionally show quantity
  if (showQuantity && quantity !== undefined) {
    return (
      <Badge 
        variant="outline"
        className={`flex items-center gap-1 text-green-700 border-green-300 bg-green-50 ${size === 'sm' ? 'text-xs' : 'text-sm'} ${className}`}
        data-testid="inventory-badge-available"
      >
        {hasLiveUpdate && (
          <RefreshCw className={`${size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} animate-spin`} />
        )}
        {quantity} {t.available}
      </Badge>
    );
  }

  return null;
}

/**
 * InventoryOverlay - Full card overlay for sold out items
 */
export function InventoryOverlay({ isSoldOut, isLowStock }) {
  const { locale } = useLanguage();
  const t = INVENTORY_TRANSLATIONS[locale] || INVENTORY_TRANSLATIONS.en;
  
  if (!isSoldOut) return null;

  return (
    <div 
      className="absolute inset-0 bg-gray-900/60 flex items-center justify-center rounded-lg z-10"
      data-testid="inventory-overlay-sold-out"
    >
      <div className="text-center">
        <XCircle className="w-10 h-10 text-white mx-auto mb-2" />
        <span className="text-white font-bold text-lg">{t.soldOut.toUpperCase()}</span>
      </div>
    </div>
  );
}

export default InventoryBadge;
